/**
 * Keeps `dailyStatsAtom` fresh in Supabase read mode.
 *
 * ATL-1165 — WHY THIS EXISTS
 *
 * The Reports overview ("Today") is rendered straight from `dailyStatsAtom`.
 * In Supabase read mode that atom had exactly ONE refresh mechanism: a
 * `postgres_changes` subscription on `daily_stats_changes`. Firestore mode has
 * never had this defect, and the difference is the whole bug: `onSnapshot` is a
 * state SYNC — it re-delivers the current document whenever the listener
 * reconnects — whereas `postgres_changes` is an event STREAM. It replays
 * nothing on rejoin and delivers nothing at all while the channel is not
 * joined, so every change that lands during an outage is lost permanently. The
 * refactor swapped one for the other without adding the resynchronisation an
 * event stream requires, and the Today card then froze at whatever the single
 * mount-time fetch returned — silently, and for as long as the session lived.
 *
 * On 2026-09-03 that cost a trading store its live sales figure: the overview
 * read 17 sales / 49,710 ₸ at 20:51 Asia/Almaty while Supabase held 19 /
 * 62,510 ₸, the gap being exactly the 20:16 and 20:33 sales. Sentry's
 * `supabase_realtime_error` event for that org at 15:45:59Z is unambiguous
 * about why: `channelsDown: 25` of 25 tracked, with
 * `daily_stats_changes:0db4d394-…` in `neverSubscribed` (not `dropped`) and
 * `outageDurationMs: 2_243_001`. The channel never joined ONCE in that
 * session.
 *
 * That last detail is what shapes this module. "Resync when the channel
 * rejoins" is necessary but would not have fired here, because there was no
 * rejoin — so the safety poll is armed by DEFAULT and is silenced only by a
 * channel that has actually reached SUBSCRIBED. A realtime stream that is
 * merely believed to be working buys nothing; one that has demonstrated it is
 * delivering buys the poll being switched off. Absence of an error is not
 * evidence of delivery.
 *
 * Steady-state cost is therefore zero: a healthy session polls never, and only
 * a session whose realtime is measurably not delivering pays for the net.
 */

/**
 * How often to refetch while realtime is not confirmed to be delivering.
 *
 * Matches the interval the pre-existing `supabaseOrgId`-missing fallback in
 * `AppStack` already used, so there is one polling cadence in this path rather
 * than two.
 */
export const DAILY_STATS_SAFETY_POLL_MS = 30_000;

/**
 * Window used to batch a burst of `daily_stats_changes` events into one
 * refetch — a multi-line sale fires the trigger repeatedly within a second.
 */
export const DAILY_STATS_CHANGE_DEBOUNCE_MS = 500;

/**
 * Whether the daily-stats realtime channel is currently joined.
 *
 * Re-exported from the channel factory rather than redeclared: `AppStack` is
 * plain JS, so a second, structurally identical declaration here would let the
 * two drift with nothing to catch it. `import type` is erased at build time —
 * this module stays free of any runtime dependency on the Supabase client.
 */
export type { RealtimeChannelState } from './supabaseClient';
import type { RealtimeChannelState } from './supabaseClient';

export interface DailyStatsFreshnessOptions {
  /**
   * Refetches the daily stats. May return a promise; a rejection is the
   * caller's to report and must not stop the poll (a till on mobile data
   * fails this call routinely, and that is exactly when the net matters).
   */
  refetch: (read?: unknown) => unknown;
  /**
   * ATL-1445 stage 3 P6 — the local view's daily-stats GENERATION: called once at the START of every
   * read this instance issues (after the single-flight guard admitted it), and its result handed to
   * `refetch` as its only argument. `AppStack` passes `beginLocalViewRefetch('dailyStats')`; its
   * `fetchDailyStats` writes the daily stats through `commitLocalViewRead` (the response carries the
   * totals and the shift status) and abandons the token when it wrote nothing, so an acknowledged
   * offline sale's totals overlay retires only on a read that started after the acknowledgement
   * (`global/Write/localView.ts`). Omitted ⇒ `refetch()` is called with no argument, as before.
   */
  beginRead?: () => unknown;
  safetyPollMs?: number;
  changeDebounceMs?: number;
}

export interface DailyStatsFreshness {
  /** A `daily_stats_changes` row arrived. Debounced. */
  onRealtimeChange(): void;
  /**
   * The channel changed state.
   *
   * `gap` is true when events may have been missed since this consumer was
   * last in sync — any SUBSCRIBED that follows a failure or an earlier
   * subscription. It is the only moment at which a rejoin is known to leave
   * the cache behind, because the stream replays nothing.
   */
  onRealtimeState(state: RealtimeChannelState, gap: boolean): void;
  /**
   * Read now, treating our copy as behind.
   *
   * Used for the initial read as well as the foreground one, so that EVERY
   * read goes through the single-flight guard and the gap sequence. The
   * initial read used to be issued directly, before the instance existed,
   * which left it the one call site that could land out of order and replace
   * correct totals with pre-sale ones.
   */
  resync(): void;
  /** The app returned to the foreground. Equivalent to `resync()`. */
  onForeground(): void;
  /** Tear down every timer. Later callbacks are inert. */
  stop(): void;
}

export function createDailyStatsFreshness(
  options: DailyStatsFreshnessOptions
): DailyStatsFreshness {
  const {
    refetch,
    beginRead,
    safetyPollMs = DAILY_STATS_SAFETY_POLL_MS,
    changeDebounceMs = DAILY_STATS_CHANGE_DEBOUNCE_MS,
  } = options;

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  /**
   * Is a read already outstanding?
   *
   * `fetchDailyStats` makes TWO sequential API calls (daily stats, then the
   * shift report), each bounded by the 30s axios timeout in
   * `global/API/apiClient.js`, so one read can take ~60s against a 30s poll.
   * Without this guard the net stacks requests on exactly the connection least
   * able to carry them, and because `setDailyStats` REPLACES the per-store
   * entry rather than merging it, two responses landing out of order let the
   * older totals win.
   */
  let inFlight = false;
  /** A run the single-flight guard displaced, owed as soon as the slot frees. */
  let pendingRun = false;
  /**
   * Which gap the current `gapOpen` refers to.
   *
   * A bare boolean is not enough, and the difference is a silent freeze rather
   * than a delay. A read ISSUED BEFORE a gap was declared can land after it,
   * report success, and close a gap its data never covered — after which
   * `syncPollState()` sees `subscribed && !gapOpen`, disarms the poll, and
   * nothing is left armed at all. The outage's sales then never appear. So a
   * read carries the sequence it was issued under and may only close that one.
   */
  let gapSeq = 0;

  /** Declare that our copy is behind, superseding any read already in flight. */
  const openGap = (): void => {
    gapOpen = true;
    gapSeq += 1;
  };

  /** Has a channel told us it is joined? */
  let subscribed = false;
  /**
   * Is there a window whose changes we have not successfully re-read yet?
   *
   * Set by anything meaning "events may have been missed" — a gapped rejoin, a
   * foreground — and cleared ONLY by a refetch that actually succeeded.
   *
   * This is the difference between the poll following EVIDENCE and the poll
   * following INTENT. The first cut disarmed the moment a catch-up was
   * *scheduled* and never looked at whether it landed, so a rejoin whose
   * catch-up failed switched the net off with nothing left to retry it — and
   * the Today card froze exactly as in the original incident, one door
   * further along. Disarming the poll is a claim to be in sync; only a
   * successful read can support it.
   */
  let gapOpen = false;

  const cancelDebounce = (): void => {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  };

  const armPoll = (): void => {
    if (stopped || pollTimer !== null) return;
    pollTimer = setInterval(run, safetyPollMs);
  };

  const disarmPoll = (): void => {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  /**
   * Poll iff we are not demonstrably in sync.
   *
   * "Demonstrably" is doing the work: a joined channel with no open gap is the
   * only state in which the stream is known to be delivering AND our copy is
   * known to be current.
   */
  const syncPollState = (): void => {
    if (stopped) return;
    if (subscribed && !gapOpen) disarmPoll();
    else armPoll();
  };

  const settle = (ok: boolean, seq: number): void => {
    // Cleared before the stopped check: a stopped instance is discarded whole,
    // but leaving this set would wedge one that is merely between reads.
    inFlight = false;
    // A result arriving after teardown belongs to a superseded organization or
    // business day. It must not clear a gap or re-arm a timer.
    if (stopped) return;
    // Only a read issued at or after the current gap can answer it.
    if (ok && seq === gapSeq) gapOpen = false;
    syncPollState();
    if (pendingRun) {
      pendingRun = false;
      run();
    }
  };

  function run(): void {
    if (stopped) return;
    // Displaced, not dropped: whatever asked for this read still needs it, and
    // waiting a full poll tick assumes a poll is armed — which it may not be.
    if (inFlight) {
      pendingRun = true;
      return;
    }
    const seq = gapSeq;
    // The refetch owns its own error reporting, so a rejection is swallowed
    // here rather than re-thrown — but it is NOT ignored: it leaves `gapOpen`
    // set, which keeps the net up. `false` counts as a failure too, because
    // `fetchDailyStats` reports its outcome by return value rather than by
    // throwing (it must catch in order to report to Sentry itself).
    inFlight = true;
    try {
      const result: unknown = beginRead ? refetch(beginRead()) : refetch();
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        void (result as Promise<unknown>).then(
          (value) => settle(value !== false, seq),
          () => settle(false, seq)
        );
      } else {
        settle(result !== false, seq);
      }
    } catch {
      settle(false, seq);
    }
  }

  const scheduleRefetch = (): void => {
    if (stopped) return;
    cancelDebounce();
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      run();
    }, changeDebounceMs);
  };

  // Fail-safe start: until a channel reports SUBSCRIBED we have no evidence the
  // stream delivers anything, and the ATL-1165 session never produced that
  // evidence at all. Assume the worst and let a healthy channel switch it off.
  armPoll();

  return {
    onRealtimeChange(): void {
      if (stopped) return;
      // A change event IS the statement that our copy is stale — a sale just
      // landed. Opening the gap here is what makes a FAILED catch-up retry:
      // without it a single dropped request left the channel subscribed, the
      // poll disarmed, and that sale invisible until the next one. That is the
      // original ATL-1165 symptom on the most common path through this module.
      openGap();
      scheduleRefetch();
    },

    onRealtimeState(state: RealtimeChannelState, gap: boolean): void {
      if (stopped) return;
      subscribed = state === 'subscribed';
      if (subscribed && gap) {
        openGap();
        scheduleRefetch();
      }
      syncPollState();
    },

    resync(): void {
      if (stopped) return;
      // A suspended app missed whatever landed while it slept, and a fresh
      // instance has nothing at all — both are gaps, open until a read succeeds.
      openGap();
      // No debounce: the caller wants the number now. A pending debounced
      // refetch is absorbed rather than left to fire a second, identical call.
      cancelDebounce();
      run();
      syncPollState();
    },

    onForeground(): void {
      this.resync();
    },

    stop(): void {
      stopped = true;
      disarmPoll();
      cancelDebounce();
    },
  };
}
